import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Link, useTheme } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import AuthScreen from '../../components/Auth/AuthScreen';
import { authTokens, emberGradient, FONT_DISPLAY, FONT_MONO } from '../../components/Auth/brandTokens';
import { BrandMark } from '../../components/Brand/Logo';

// Matches the server password policy so we fail fast before the API rejects.
function passwordStrengthError(p: string): string | null {
  if (p.length < 8) return 'Use 8+ characters';
  if (!/[a-z]/.test(p)) return 'Add a lowercase letter';
  if (!/[A-Z]/.test(p)) return 'Add an uppercase letter';
  if (!/[0-9]/.test(p)) return 'Add a number';
  if (!/[^A-Za-z0-9]/.test(p)) return 'Add a symbol';
  return null;
}

const errorText = (code?: string) => {
  switch (code) {
    case 'invalid_or_expired_token': return 'This reset link is invalid or has expired — request a new one.';
    default: return 'Could not reset your password. Please try again.';
  }
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();
  const themeMode = useTheme().palette.mode === 'dark' ? 'dark' : 'light';
  const t = authTokens(themeMode);
  const token = new URLSearchParams(location.search).get('token') || '';

  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pe = passwordStrengthError(password);
    if (pe) { setError(pe); return; }
    setSubmitting(true);
    setError(null);
    const res = await resetPassword(token, password);
    setSubmitting(false);
    if (res.ok) navigate('/');
    else setError(errorText(res.error));
  };

  const mono = { fontFamily: FONT_MONO, textTransform: 'uppercase' as const };
  const row = {
    display: 'flex', alignItems: 'center', gap: '6px',
    borderBottom: `1.5px solid ${error ? t.err : t.line}`, transition: 'border-color .2s',
    '&:focus-within': { borderBottomColor: t.ember },
  };
  const plainInput = { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: '10px 2px', fontSize: 16, fontFamily: "'Hanken Grotesk', sans-serif", color: t.text, outline: 'none' } as const;

  return (
    <AuthScreen>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, fontFamily: "'Hanken Grotesk', sans-serif", color: t.text }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 999, display: 'grid', placeItems: 'center', background: t.emberSoft, border: `1px solid ${t.line}` }}>
          <BrandMark size={26} color={t.ember} />
        </Box>
        <Box>
          <Box component="h2" sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 26, letterSpacing: '-0.02em', m: 0, color: t.text }}>Set a new password</Box>
          <Box component="p" sx={{ m: '6px 0 0', fontSize: 14.5, color: t.muted, lineHeight: 1.5 }}>
            {token ? 'Choose a strong password — you’ll be signed in right after.' : 'This reset link is missing or invalid.'}
          </Box>
        </Box>

        {!token ? (
          <Box sx={{ fontSize: 13.5, color: t.muted, lineHeight: 1.5 }}>
            Request a fresh link from <Link component={RouterLink} to="/login" sx={{ color: t.ember, fontWeight: 600 }} underline="hover">sign in</Link> → “Forgot?”.
          </Box>
        ) : (
          <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              <Box component="label" htmlFor="df-newpw" sx={{ ...mono, fontSize: 10.5, letterSpacing: '0.13em', color: t.muted }}>New password</Box>
              <Box sx={row}>
                <Box component="input" id="df-newpw" type={showPw ? 'text' : 'password'} autoComplete="new-password" autoFocus
                  placeholder="Create a password"
                  value={password} onChange={(e: any) => { setPassword(e.target.value); setError(null); }} sx={plainInput} />
                <Box component="button" type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? 'Hide password' : 'Show password'}
                  sx={{ background: 'none', border: 'none', color: t.muted, cursor: 'pointer', fontSize: 10.5, fontFamily: FONT_MONO, letterSpacing: '0.1em', p: '4px 2px' }}>
                  {showPw ? 'HIDE' : 'SHOW'}
                </Box>
              </Box>
            </Box>

            {error && <Box sx={{ fontSize: 12.5, color: t.err, fontWeight: 600 }}>{error}</Box>}

            <Box component="button" type="submit" disabled={submitting || !password}
              sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '9px', width: '100%', minHeight: 52, border: 'none', borderRadius: '2px', cursor: submitting || !password ? 'default' : 'pointer', fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 16, color: t.onEmber, background: emberGradient(t), boxShadow: `0 8px 24px -8px ${t.shadow}`, opacity: submitting || !password ? 0.65 : 1, transition: 'filter .2s' }}>
              {submitting ? 'Resetting…' : 'Reset password'} <Box component="span" aria-hidden sx={{ fontWeight: 700 }}>→</Box>
            </Box>
          </Box>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" sx={{ color: t.ember, fontWeight: 700, fontSize: 14 }} underline="hover">Back to sign in</Link>
        </Box>
      </Box>
    </AuthScreen>
  );
};

export default ResetPassword;
